import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../lib/AuthContext';
import { supabase } from '../../../lib/supabase';
import Avatar from '../display/Avatar';
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Workspace {
  workspace_id: string;
  workspace_name: string;
  workspace_type: string;
  logo_url: string | null;
}

export const MobileSidebar: React.FC<MobileSidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [teamWorkspaces, setTeamWorkspaces] = useState<Workspace[]>([]);
  const [showLogout, setShowLogout] = React.useState(false);
  const logoutButtonRef = React.useRef<HTMLDivElement>(null);
  const [expandedSections, setExpandedSections] = React.useState<string[]>([]);

  React.useEffect(() => {
    const fetchWorkspaces = async () => {
      if (!user) return;

      // Fetch team workspaces directly with a single query
      const { data: workspaces, error: workspacesError } = await supabase
        .from('workspaces')
        .select('workspace_id, workspace_name, workspace_type, logo_url')
        .eq('workspace_type', 'team')
        .in('workspace_id', (
          await supabase
            .from('workspace_users')
            .select('workspace_id')
            .eq('user_id', user.id)
        ).data?.map(row => row.workspace_id) || []
        );

      if (workspacesError) {
        console.error('Error fetching workspaces:', workspacesError);
        return;
      }

      setTeamWorkspaces(workspaces || []);
    };

    fetchWorkspaces();
  }, [user]);

  // Handle click outside for logout button
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (logoutButtonRef.current && !logoutButtonRef.current.contains(event.target as Node)) {
        setShowLogout(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const menuItems = [
    // {
    //   icon: <FolderIcon className="h-6 w-6" />,
    //   label: 'Projects',
    //   path: '/projects',
    // },
    // {
    //   icon: <RectangleStackIcon className="h-6 w-6" />,
    //   label: 'Resources',
    //   path: '/resources',
    // },
    {
      icon: <Cog6ToothIcon className="h-6 w-6" />,
      label: 'Settings',
      path: '/settings',
    },
  ];

  const helpItems = [
    {
      label: 'Help Center',
      href: 'https://help.holito.app/en/',
    },
    {
      label: 'Discord Community',
      href: 'https://discord.com/invite/RvgwKKZSdC',
    },
    {
      label: 'Suggest a Feature',
      href: 'https://holito.canny.io/',
    },
    {
      label: 'Status Page',
      href: 'https://status.holito.app/',
    },
  ];

  const legalItems = [
    {
      label: 'Privacy Policy',
      href: 'https://holito.app/privacy/',
    },
    {
      label: 'Terms of Service',
      href: 'https://holito.app/terms/',
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 md:hidden ${
          isOpen ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'
        } z-40`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-[280px] bg-white z-50 transform transition-transform duration-300 md:hidden flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* User Profile - Fixed */}
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1 min-w-0 pr-2">
              <Avatar
                firstName={user?.user_metadata?.first_name || 'Guest'}
                lastName={user?.user_metadata?.last_name || ''}
                profileImageUrl={user?.user_metadata?.profile_image_url}
                size="medium"
              />
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-gray-900 truncate">
                  {user?.user_metadata?.first_name} {user?.user_metadata?.last_name}
                </h2>
                <p className="text-sm text-gray-500 break-words">{user?.email}</p>
              </div>
            </div>
            <div className="relative flex-shrink-0" ref={logoutButtonRef}>
              <button
                onClick={() => setShowLogout(!showLogout)}
                className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"
              >
                <EllipsisHorizontalIcon className="h-6 w-6 text-gray-500" />
              </button>
              {showLogout && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-10">
                  <button
                    onClick={signOut}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Team Workspaces Section - Now in scrollable area */}
          <div className="p-4 border-b border-gray-100">
            <div className="text-sm font-medium text-gray-900 mb-2">Your Team Workspaces</div>
            <div className="space-y-2">
              {teamWorkspaces.length > 0 ? (
                teamWorkspaces.map((workspace) => (
                  <button
                    key={workspace.workspace_id}
                    onClick={() => {
                      handleNavigation(`/workspace/${workspace.workspace_id}`);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      {workspace.logo_url ? (
                        <img
                          src={workspace.logo_url}
                          alt={workspace.workspace_name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-base text-xs">
                            {workspace.workspace_name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <span className="text-sm text-gray-700">
                        {workspace.workspace_name}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center">No team workspaces yet</p>
              )}
            </div>
          </div>

          {/* Menu Items */}
          <nav className="px-4 py-2">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className="flex items-center w-full px-2 py-2.5 text-[15px] text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
              >
                <div className="w-5 h-5">{item.icon}</div>
                <span className="ml-2">{item.label}</span>
              </button>
            ))}

            {/* Help & Support Section */}
            <div className="border-t mt-4 pt-4">
              <button
                onClick={() => toggleSection('help')}
                className="flex items-center justify-between w-full px-2 py-2 text-[13px] font-semibold text-gray-900 cursor-pointer"
              >
                <span>Help and support</span>
                <ChevronDownIcon
                  className={`h-5 w-5 transform transition-transform ${
                    expandedSections.includes('help') ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expandedSections.includes('help') && (
                <div className="mt-2 space-y-1">
                  {helpItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-1.5 text-[15px] text-gray-600 hover:text-gray-900 cursor-pointer"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Legal & Policies Section */}
            <div className="mb-4 pt-4">
              <button
                onClick={() => toggleSection('legal')}
                className="flex items-center justify-between w-full px-2 py-2 text-[13px] font-semibold text-gray-900 cursor-pointer"
              >
                <span>Legal & Policies</span>
                <ChevronDownIcon
                  className={`h-5 w-5 transform transition-transform ${
                    expandedSections.includes('legal') ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expandedSections.includes('legal') && (
                <div className="mt-2 space-y-1">
                  {legalItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-1.5 text-[15px] text-gray-600 hover:text-gray-900 cursor-pointer"
                    >
                      {item.label}
                    </a>
                  ))}
                  <p className="px-2 py-1.5 text-xs text-gray-500 mt-4 mb-20">
                    © {new Date().getFullYear()} holito. All rights reserved.
                  </p>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};